import { fork, takeLatest, call, put, all, select } from "redux-saga/effects";
import analyticsApi from "./analyticsApi";

import {
  fetchBasicAnalyticsRequest,
  fetchBasicAnalyticsSuccess,
  fetchBasicAnalyticsFailure,
  fetchPracticeSolutionRequest,
  fetchPracticeSolutionSuccess,
  fetchPracticeSolutionFailure,
  fetchTopicWiseComparisonRequest,
  fetchTopicWiseComparisonSuccess,
  fetchTopicWiseComparisonFailure,
  fetchSubjectWiseAnalysisRequest,
  fetchSubjectWiseAnalysisSuccess,
  fetchSubjectWiseAnalysisFailure,
  fetchOverallAnalyticsRequest,
  fetchOverallAnalyticsSuccess,
  fetchOverallAnalyticsFailure,
  fetchQuestionAnalyticsRequest,
  fetchQuestionAnalyticsSuccess,
  fetchQuestionAnalyticsFailure,
} from "./analyticsSlice";

/* ================= PAYLOAD BUILDER ================= */
const buildPyqPayload = (examState, analyticsState) => {
  const paperType = examState.testType?.id;
  const source = examState.testDetails?.source;
  const isTopicWisePyq = Boolean(examState.testDetails?.setId);
  const plannerTestId =
    examState.testDetails?.planner_test_id ||
    examState.testDetails?.testId ||
    examState.testId;
  const isAitsStyleTest =
    paperType === 3 ||
    (!isTopicWisePyq &&
      Boolean(plannerTestId) &&
      source !== "topicwise" &&
      source !== "yearwise" &&
      // Dashboard deep-links only use AITS-style when paper type is explicitly AITS/OTS (3).
      source !== "dashboard");
  const selectedFromBasic =
    analyticsState?.basicAnalytics?.data?.selected_attempt;
  // selected_attempt is already the attempt id used by attempt_count — do not +1.
  const defaultAttemptCount =
    selectedFromBasic !== undefined && selectedFromBasic !== null
      ? Number(selectedFromBasic)
      : undefined;

  /* ---------- AITS / RE-NEET style tests ---------- */
  if (isAitsStyleTest || (source === "dashboard" && Number(paperType) === 3)) {
    return {
      paperType,
      paperId: examState.testDetails?.paperId || examState.paperId,
      plannerTestId,
      isOffline: 0,
      attemptCount: defaultAttemptCount,
    };
  }

  /* ---------- PYQ ---------- */
  return {
    paperType,
    paperId: isTopicWisePyq ? undefined : examState.paperId,
    setId: isTopicWisePyq ? examState.testDetails.setId : undefined,
  };
};

const resolveAnalyticsPayload = (examState, analyticsState, actionPayload = {}) => {
  let payload = buildPyqPayload(examState, analyticsState);

  if (actionPayload?.paper_type ?? actionPayload?.paperType) {
    payload.paperType = actionPayload.paper_type ?? actionPayload.paperType;
  }
  if (actionPayload?.paper_id ?? actionPayload?.paperId) {
    payload.paperId = actionPayload.paper_id ?? actionPayload.paperId;
    payload.setId = undefined;
  }
  if (actionPayload?.planner_test_id) {
    payload.plannerTestId = actionPayload.planner_test_id;
  }
  if (actionPayload?.set_id ?? actionPayload?.setId) {
    payload = {
      ...payload,
      paperType: actionPayload.paper_type ?? actionPayload.paperType ?? payload.paperType,
      setId: actionPayload.set_id ?? actionPayload.setId,
      paperId: undefined,
    };
  }
  if (actionPayload?.attempt_count !== undefined) {
    payload.attemptCount = actionPayload.attempt_count;
  } else if (actionPayload?.selected_attempt !== undefined) {
    // selected_attempt is the attempt id; pass through as attempt_count (no +1).
    payload.attemptCount = Number(actionPayload.selected_attempt);
  }

  return payload;
};

/* ---------------- BASIC ANALYTICS ---------------- */
// function* fetchBasicAnalyticsWorker() {
//   try {
//     const state = yield select((s) => s.examSlice);
//     const response = yield call(
//       analyticsApi.getBasicAnalytics,
//       buildPyqPayload(state)
//     );
//     yield put(fetchBasicAnalyticsSuccess(response));
//   } catch (e) {
//     yield put(fetchBasicAnalyticsFailure(e.message));
//   }
// }
function* fetchBasicAnalyticsWorker(action) {
  try {
    const examState = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const payload = resolveAnalyticsPayload(
      examState,
      analyticsState,
      action.payload || {}
    );

    const response = yield call(
      analyticsApi.getBasicAnalytics,
      payload
    );

    yield put(fetchBasicAnalyticsSuccess(response));
  } catch (e) {
    yield put(fetchBasicAnalyticsFailure(e.message));
  }
}

/* ---------------- PRACTICE SOLUTION ---------------- */
function* fetchPracticeSolutionWorker(action) {
  try {
    const examState = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const payload = resolveAnalyticsPayload(
      examState,
      analyticsState,
      action.payload || {}
    );
    const response = yield call(
      analyticsApi.getPracticeSolution,
      payload
    );
    yield put(fetchPracticeSolutionSuccess(response));
  } catch (e) {
    yield put(fetchPracticeSolutionFailure(e.message));
  }
}

/* ---------------- TOPIC WISE ---------------- */
function* fetchTopicWiseComparisonWorker(action) {
  try {
    const examState = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const response = yield call(
      analyticsApi.getTopicWiseComparison,
      resolveAnalyticsPayload(examState, analyticsState, action.payload || {})
    );
    yield put(fetchTopicWiseComparisonSuccess(response));
  } catch (e) {
    yield put(fetchTopicWiseComparisonFailure(e.message));
  }
}

/* ---------------- SUBJECT WISE ---------------- */
function* fetchSubjectWiseAnalysisWorker(action) {
  try {
    const examState = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const response = yield call(
      analyticsApi.getSubjectWiseComparison,
      resolveAnalyticsPayload(examState, analyticsState, action.payload || {})
    );
    yield put(fetchSubjectWiseAnalysisSuccess(response));
  } catch (e) {
    yield put(fetchSubjectWiseAnalysisFailure(e.message));
  }
}

/* ---------------- OVERALL ---------------- */
function* fetchOverallAnalyticsWorker(action) {
  try {
    const examState = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const response = yield call(
      analyticsApi.getOverallAnalytics,
      {
        ...resolveAnalyticsPayload(examState, analyticsState, action.payload || {}),
        filter_by: action.payload?.filter_by || "tpq",
      }
    );
    yield put(fetchOverallAnalyticsSuccess(response));
  } catch (e) {
    yield put(fetchOverallAnalyticsFailure(e.message));
  }
}

/* ---------------- QUESTION ANALYTICS ---------------- */
function* fetchQuestionAnalyticsWorker(action) {
  try {
    const state = yield select((s) => s.examSlice);
    const analyticsState = yield select((s) => s.analyticsSlice);
    const resolvedSetId =
      action.payload?.setId ||
      state.testDetails?.setId ||
      state.testDetails?.set_id;
    const resolvedPaperId =
      action.payload?.paperId ||
      state.paperId ||
      state.testDetails?.paper_id ||
      state.testDetails?.paperId;
    const selectedFromBasic =
      analyticsState.basicAnalytics?.data?.selected_attempt;
    const attemptCount =
      action.payload?.attempt_count !== undefined
        ? action.payload.attempt_count
        : selectedFromBasic !== undefined && selectedFromBasic !== null
        ? Number(selectedFromBasic)
        : undefined;
    const response = yield call(analyticsApi.getQuestionAnalytics, {
      paperType: action.payload?.paperType || state.testType?.id,
      paperId: resolvedPaperId,
      setId: resolvedSetId,
      qid: action.payload.qid,
      isOffline: action.payload?.is_offline ?? 0,
      attemptCount,
    });
    yield put(fetchQuestionAnalyticsSuccess(response));
  } catch (e) {
    yield put(fetchQuestionAnalyticsFailure(e.message));
  }
}

/* ---------------- WATCHER ---------------- */
function* watchAnalyticsSaga() {
  yield takeLatest(fetchBasicAnalyticsRequest.type, fetchBasicAnalyticsWorker);
  yield takeLatest(fetchPracticeSolutionRequest.type, fetchPracticeSolutionWorker);
  yield takeLatest(fetchTopicWiseComparisonRequest.type, fetchTopicWiseComparisonWorker);
  yield takeLatest(fetchSubjectWiseAnalysisRequest.type, fetchSubjectWiseAnalysisWorker);
  yield takeLatest(fetchOverallAnalyticsRequest.type, fetchOverallAnalyticsWorker);
  yield takeLatest(fetchQuestionAnalyticsRequest.type, fetchQuestionAnalyticsWorker);
}

export default function* analyticsSaga() {
  yield all([fork(watchAnalyticsSaga)]);
}
